import type { Tour } from 'nextstepjs';
import type { OnboardingState } from '../onboarding';
import {
  getCompleteOnboardingTour,
  getStoreOnlyTour,
  getPluginOnlyTour,
} from './complete-onboarding-tour';

/**
 * Tour Manager
 * Orchestrates which tours to show based on onboarding state and current URL
 */

interface TourConfig {
  tours: Tour[];
  startTour: string; // Which tour to start with
}

/**
 * Get the appropriate tour based on onboarding intent
 */
export function getTourForOnboardingIntent(
  state: OnboardingState,
  currentPath: string,
  appName?: string,
): TourConfig | null {
  if (!state.intent) return null;

  const { intent } = state;
  const pluginName = intent.pluginName;

  // Plugin installation flow (from marketplace)
  if (intent.type === 'plugin_install') {
    // On store creation page - show store creation tour
    if (currentPath.includes('/stores/create')) {
      return {
        tours: getStoreOnlyTour(),
        startTour: 'store-creation',
      };
    }

    // On plugins page - show plugin installation tour
    if (currentPath.includes('/plugins')) {
      return {
        tours: getPluginOnlyTour(pluginName),
        startTour: 'plugin-installation',
      };
    }
  }

  // Generic store creation flow (direct signup without plugin)
  if (intent.type === 'store_create') {
    // On store creation page - show store creation tour
    if (currentPath.includes('/stores/create')) {
      return {
        tours: getStoreOnlyTour(),
        startTour: 'store-creation',
      };
    }

    // On stores list - show helpful guidance
    if (currentPath.includes('/stores') && !currentPath.includes('/stores/')) {
      // No tour for stores list in generic flow, user can create store manually
      return null;
    }
  }

  return null;
}

/**
 * Determine if a tour should be shown based on state and URL
 */
export function shouldShowTour(
  state: OnboardingState | null,
  currentPath: string,
): boolean {
  if (!state?.intent) return false;

  // Don't show tours on these pages
  const excludedPaths = ['/sign-in', '/sign-up', '/billing', '/setup-payment'];
  if (excludedPaths.some((path) => currentPath.includes(path))) {
    return false;
  }

  // Show tour ONLY on these specific pages
  // ✅ /no-organization - account creation tour
  // ✅ /stores/create - store creation tour
  // ✅ /org/{slug}/stores (exact) - stores list page
  // ✅ /org/{slug}/stores/{id}/plugins - plugin marketplace (plugin installation tour)
  // ❌ /org/{slug}/stores/{id}/general/* - NOT for store detail pages like products

  if (currentPath.includes('/stores/create')) return true;
  if (currentPath.match(/\/stores\/[^/]+\/plugins/)) return true; // Plugin page
  if (currentPath.match(/\/stores$/) || currentPath.match(/\/stores\/$/)) return true; // Stores list only

  return false;
}

/**
 * Get the active tour configuration
 */
export function getActiveTour(
  state: OnboardingState | null,
  currentPath: string,
  appName?: string,
): TourConfig | null {
  if (!state || !shouldShowTour(state, currentPath)) {
    return null;
  }

  return getTourForOnboardingIntent(state, currentPath, appName);
}
